
import { useState } from "react"
import { X } from "lucide-react"
import UploadStep from "./steps/UploadStep"
import SchemaStep from "./steps/SchemaStep"
import PreviewStep from "./steps/PreviewStep"
import ConfirmStep from "./steps/ConfirmStep"
import type { ProjectImportRow } from "../hooks/types"

interface ProjectImportWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function ProjectImportWizard({ isOpen, onClose, onSuccess }: ProjectImportWizardProps) {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [excelFile, setExcelFile] = useState<File | null>(null)
  const [sampleHeaders, setSampleHeaders] = useState<string[]>([])
  const [sampleRows, setSampleRows] = useState<string[][]>([])
  const [parsedRecords, setParsedRecords] = useState<ProjectImportRow[]>([])
  const [error, setError] = useState<string>("")

  const handleUploadSuccess = (file: File, headers: string[], rows: string[][]) => {
    setExcelFile(file)
    setSampleHeaders(headers)
    setSampleRows(rows)
    setCurrentStep(2)
  }

  const handleSchemaSuccess = () => {
    setCurrentStep(3)
  }

  const handlePreviewSuccess = (records: ProjectImportRow[]) => {
    setParsedRecords(records)
    setCurrentStep(4)
  }

  const handleConfirmSuccess = () => {
    onSuccess()
    resetWizard()
  }

  const resetWizard = () => {
    setCurrentStep(1)
    setExcelFile(null)
    setSampleHeaders([])
    setSampleRows([])
    setParsedRecords([])
    setError("")
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Import Projects from Excel</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

        <div className="py-4">
          {/* Step indicator */}
          <div className="flex mb-8">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex-1 relative">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${
                    currentStep >= step ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {step}
                </div>
                <div className="text-xs text-center mt-2">
                  {step === 1 && "Upload"}
                  {step === 2 && "Validate"}
                  {step === 3 && "Preview"}
                  {step === 4 && "Confirm"}
                </div>
                {step < 4 && (
                  <div
                    className={`absolute top-4 left-1/2 w-full h-0.5 ${
                      currentStep > step ? "bg-blue-600" : "bg-gray-200"
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>

          {currentStep === 1 && <UploadStep onSuccess={handleUploadSuccess} onError={setError} />}

          {currentStep === 2 && excelFile && (
            <SchemaStep
              excelFile={excelFile}
              sampleHeaders={sampleHeaders}
              sampleRows={sampleRows}
              onBack={() => setCurrentStep(1)}
              onSuccess={handleSchemaSuccess}
              onError={setError}
            />
          )}

          {currentStep === 3 && excelFile && (
            <PreviewStep
              excelFile={excelFile}
              onBack={() => setCurrentStep(2)}
              onSuccess={handlePreviewSuccess}
              onError={setError}
            />
          )}

          {currentStep === 4 && (
            <ConfirmStep
              parsedRecords={parsedRecords}
              onBack={() => setCurrentStep(3)}
              onSuccess={handleConfirmSuccess}
              onError={setError}
            />
          )}
        </div>
      </div>
    </div>
  )
}
